import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  TableContainer,
  Table,
  Card,
  CardContent,
  Badge
} from '@mui/material';
import { FaCheck, FaPlus, FaTrash } from 'react-icons/fa';
import ConfigureExtrasDialog from './ConfigureExtrasDialog';
import EditableTableCell from './EditableTableField';
import { getDisplayPrice } from '../Common/Price';

export function ItemSelectionSection({
  selectedItems = [],
  menuItems = [],
  categories = [],
  optionId,
  onItemSelect,
  onMultipleSelect,
  onExtraPriceChange
}) {

  const [configureDialog, setConfigureDialog] = useState({
    open: false,
    items: [],
    categoryName: '',
    onConfirm: null
  });

  // Grouper les items par catégorie
  const itemsByCategory = useMemo(() => {
    return menuItems.reduce((acc, item) => {
      const categoryId = item.categoryId;
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(item);
      return acc;
    }, {});
  }, [menuItems]);

  // Catégories avec statut de sélection et nombre d'items
  const categoriesWithInfo = useMemo(() => {
    return categories
      .map(category => {
        const categoryItems = itemsByCategory[category.id] || [];
        const selectedCount = categoryItems.filter(item => 
          selectedItems.some(si => si.id === item.id)
        ).length;
        
        return {
          ...category,
          itemCount: categoryItems.length,
          selectedCount,
          isFullySelected: selectedCount === categoryItems.length && categoryItems.length > 0
        };
      })
      .filter(category => category.itemCount > 0);
  }, [categories, itemsByCategory, selectedItems]);

  const handleCategorySelect = (category) => {
    const categoryItems = itemsByCategory[category.id] || [];
    
    if (category.isFullySelected) {
      // Désélectionner la catégorie
      const itemIds = categoryItems.map(item => item.id);
      onMultipleSelect(optionId, itemIds, false);
    } else {
      // Ouvrir la dialog de configuration
      setConfigureDialog({
        open: true,
        items: categoryItems,
        categoryName: category.name,
        onConfirm: (configuredItems) => {
          // Transformer les items configurés en format attendu
          const itemsToAdd = configuredItems.map(({ id, extraPrice }) => ({
            id,
            extraPrice,
            optionId // Ajouter l'optionId pour le tracking
          }));
          
          // Appeler onMultipleSelect avec les items configurés
          onMultipleSelect(
            optionId,
            itemsToAdd,
            true
          );
        }
      });
    }
  };

  const handleDialogClose = () => {
    setConfigureDialog(prev => ({ ...prev, open: false }));
  };

  const handleDialogConfirm = (configuredItems) => {
    if (configureDialog.onConfirm) {
      configureDialog.onConfirm(configuredItems);
    }
    handleDialogClose();
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Grille des catégories */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {categoriesWithInfo.map(category => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <Card 
              variant="outlined" 
              sx={{ 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                bgcolor: category.isFullySelected ? 'primary.50' : 'background.paper',
                position: 'relative'
              }}
              onClick={() => handleCategorySelect(category)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6" component="div">
                    {category.name}
                  </Typography>
                  <Badge 
                    badgeContent={category.selectedCount} 
                    color="primary"
                    max={99}
                    invisible={category.selectedCount === 0}
                  >
                    <IconButton 
                      size="small"
                      color={category.isFullySelected ? "primary" : "default"}
                    >
                      {category.isFullySelected ? <FaCheck /> : <FaPlus />}
                    </IconButton>
                  </Badge>
                </Box>
                <Typography color="text.secondary" variant="body2">
                  {category.itemCount} item{category.itemCount > 1 ? 's' : ''}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tableau des items sélectionnés */}
      {selectedItems.length > 0 && (
        <TableContainer component={Paper} elevation={3}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell align="right">Prix de base</TableCell>
                <TableCell align="right">Supplément</TableCell>
                <TableCell width={70}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedItems.map((selectedItem, index) => {
                const menuItem = menuItems.find(item => item.id === selectedItem.id);
                if (!menuItem) return null;

                const extraPrice = selectedItem.extraPrice || 0;

                return (
                  <TableRow key={selectedItem.id}>
                    <TableCell>{menuItem.name}</TableCell>
                    <TableCell align="right">{getDisplayPrice(menuItem)}</TableCell>
                    <TableCell align="right">
                      <EditableTableCell
                        value={extraPrice}
                        onChange={(value) => onExtraPriceChange(optionId, selectedItem.id, parseFloat(value) || 0)}
                        type="number"
                        align="right"
                        startAdornment="+"
                        endAdornment="€"
                        InputProps={{
                          startAdornment: '+',
                          endAdornment: '€',
                          inputProps: {
                            min: 0,
                            step: 0.5,
                            style: { 
                              width: '80px',
                              // CSS pour masquer les boutons d'incrémentation/décrémentation
                              '-moz-appearance': 'textfield',
                              '&::-webkit-outer-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                              },
                              '&::-webkit-inner-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                              },
                            },
                            onWheel: (e) => {
                              e.preventDefault();
                              e.target.blur();
                            }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Retirer">
                        <IconButton
                          size="small"
                          onClick={() => onItemSelect(selectedItem.id)}
                          color="error"
                        >
                          <FaTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog de configuration */}
      <ConfigureExtrasDialog
        open={configureDialog.open}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        items={configureDialog.items}
        categoryName={configureDialog.categoryName}
      />
    </Box>
  );
}